// src/components/RepoInput.jsx

import React, { useEffect, useMemo, useState } from "react";
import { getCurrentUser, startAnalysis } from "../api/reportApi";

export default function RepoInput({ onStarted }) {
    const [sourceType, setSourceType] = useState("remote");
    const [repoPathOrUrl, setRepoPathOrUrl] = useState("");
    const [branch, setBranch] = useState("");
    const [token, setToken] = useState("");
    const [starting, setStarting] = useState(false);
    const [error, setError] = useState("");
    const [validationErrors, setValidationErrors] = useState({});
    const loading = starting;
    const user = getCurrentUser();
    const email = user?.email || "";
    const owner = useMemo(() => email ? email.split("@")[0] : "", [email]);
    const repoName = useMemo(() => {
        if (!repoPathOrUrl) return "";
        const normalized = repoPathOrUrl.trim().replace(/\.git$/, "").replace(/\/+$/, "");
        const segments = normalized.split("/");
        return segments[segments.length - 1] || "";
    }, [repoPathOrUrl]);

    useEffect(() => {
        setError("");
        setValidationErrors({});
    }, [repoPathOrUrl, branch, token, sourceType]);

    const isValidRemoteUrl = (value) => {
        try {
            const url = new URL(value);
            return url.protocol === "https:" || url.protocol === "http:";
        } catch {
            return false;
        }
    };

    const start = async () => {
        if (loading) return;
        setError("");
        setValidationErrors({});
        if (!email) { setError("Authentication required"); return; }

        try {
            setStarting(true);
            if (sourceType === "remote") {
                const url = repoPathOrUrl.trim();
                if (!isValidRemoteUrl(url)) {
                    setValidationErrors({ repoPathOrUrl: "Remote repo must be a valid HTTP/HTTPS URL" });
                    return;
                }
                if (!branch.trim()) {
                    setValidationErrors({ branch: "Branch is required" });
                    return;
                }
            }

            const payload = sourceType === "remote"
                ? { email, sourceType, owner, repoName, repoPathOrUrl: repoPathOrUrl.trim(), branch: branch.trim(), personalAccessToken: token.trim() }
                : { email, sourceType, owner, repoName, repoPathOrUrl: repoPathOrUrl.trim(), branch: "__local_branch__", personalAccessToken: null };

            const res = await startAnalysis(payload);
            onStarted(res.jobId);
        } catch (err) {
            console.error("START ERROR:", err);
            if (err.validation) setValidationErrors(err.validation);
            else setError(err.message || "Failed to start analysis");
        } finally {
            setStarting(false);
        }
    };

    return (
        <div className="w-full max-w-5xl mx-auto px-4 py-6">
            <div className="repo-input-card space-y-4">
                <div className="repo-input-tabs">
                    <button type="button" onClick={() => { setSourceType("remote"); setRepoPathOrUrl(""); }} className={sourceType === "remote" ? "active" : ""}>Remote Repo</button>
                    <button type="button" onClick={() => { setSourceType("local"); setRepoPathOrUrl(""); }} className={sourceType === "local" ? "active" : ""}>Local Repo</button>
                </div>

                <div>
                    <label className="repo-input-label">{sourceType === "remote" ? "Repository URL" : "Local Repository Path"}</label>
                    <input type="text" placeholder={sourceType === "remote" ? "https://example.com/owner/repo" : "/Users/project/repo"} value={repoPathOrUrl} onChange={(e) => setRepoPathOrUrl(e.target.value)} disabled={loading} className="repo-input-field" />
                    {validationErrors.repoPathOrUrl && <div className="text-sm text-red-400 mt-2">{validationErrors.repoPathOrUrl}</div>}
                </div>

                {sourceType === "remote" && (
                    <>
                        <div>
                            <label className="repo-input-label">Branch</label>
                            <input type="text" placeholder="main" value={branch} onChange={(e) => setBranch(e.target.value)} disabled={loading} className="repo-input-field" />
                            {validationErrors.branch && <div className="text-sm text-red-400 mt-2">{validationErrors.branch}</div>}
                        </div>

                        <div className="repo-input-private space-y-3">
                            <div className="repo-input-private-title"><span aria-hidden="true">🔒</span> Private repository access</div>
                            <label className="repo-input-label">Personal Access Token (PAT)</label>
                            <input type="password" placeholder="Enter token (optional)" value={token} onChange={(e) => setToken(e.target.value)} disabled={loading} autoComplete="new-password" className="repo-input-field" />
                            <p className="repo-input-help">For private GitHub and GitLab repositories. Not required for public repositories.</p>
                            {validationErrors.personalAccessToken && <div className="text-sm text-red-400">{validationErrors.personalAccessToken}</div>}
                        </div>
                    </>
                )}

                <div className="repo-input-meta space-y-2">
                    <div>Email: {email || "-"}</div>
                    <div>Owner: {owner || "-"}</div>
                    <div>Repo: {repoName || "-"}</div>
                </div>

                {!!error && <div className="text-sm text-red-400 whitespace-pre-wrap">{error}</div>}

                <button type="button" onClick={start} disabled={loading || !repoPathOrUrl.trim() || !email} className="repo-input-submit">
                    {starting ? "Starting..." : "Start Analysis"}
                </button>
            </div>
        </div>
    );
}